import {
  CHIP_DENOMS,
  CHIP_SETS,
  DECKS,
  DECK_SUITS,
  FELTS,
  readTheme,
  type BackPattern,
  type CasinoTheme,
  type ChipDenom,
  type ChipFace,
  type ChipSetId,
  type DeckId,
  type DeckSuit,
  type FeltId,
} from '@tv-casino/shared';

/**
 * A casino's theme as one renderer-neutral record. The web turns it into CSS
 * custom properties (`apps/web/src/lib/themeStyle.ts`, pinned by its tests);
  the native shells read it directly through their theme provider. Every
 * renderer draws the same parts from this one place, so a palette change
 * lands once and shows up as a diff in three suites — never three
 * hand-edited palettes.
 */

/** The house colours every screen prints, as the web's stylesheet ships them. */
export interface HousePalette {
  /** Brassage and headlines — the TV's kicker, the join code, a pot. */
  gold: string;
  /** The gold's lit state: hovers, the acting seat's ring at its brightest. */
  goldBright: string;
  /** Body text and card paper off the felt. */
  cream: string;
  /** Dark ink on gold and cream surfaces. */
  ink: string;
  /** Losses, refusals, the cut card's mouth. */
  danger: string;
  /** Wins and the paid mark. */
  ok: string;
  /** An unclaimed seat plate's wash. */
  seatEmpty: string;
}

/**
 * The house type. Web joins each stack into a CSS `font-family`; native
 * picks the first face its platform resolves (a serif display line is part
 * of the room's look, not a web accident).
 */
export interface HouseFonts {
  display: string[];
  ui: string[];
}

export interface ThemeTokens {
  felt: {
    id: FeltId;
    /** The table's edge, and `r, g, b` for the washes over it. */
    deep: string;
    deepRgb: string;
    /** The cloth across the felt. */
    base: string;
    /** Under the lamp. */
    light: string;
  };
  chips: {
    id: ChipSetId;
    /** A face per denomination — rail chips wear these; felt chips wear
        their bettor's hue instead (that is who bet, not how much). */
    faces: Record<ChipDenom, ChipFace>;
  };
  deck: {
    id: DeckId;
    back: {
      /** Under the pattern; may name a felt variable, so resolve it beside
          the felt (web does, as a CSS value; native mixes it itself). */
      ground: string;
      /** The CSS background — web only. */
      pattern: string;
      /** The same pattern as geometry, for renderers with no CSS. */
      backPattern: BackPattern;
      border: string;
    };
    face: {
      paper: string;
      edge: string;
      suits: Record<DeckSuit, string>;
    };
  };
  palette: HousePalette;
  /** The card and panel corner, in px at the web's natural scale. */
  radius: number;
  font: HouseFonts;
}

/**
 * The house colours, exactly as `apps/web/src/styles/global.css` ships them —
 * this record is where they are pinned, so a restyle is a diff here and the
 * web's `:root` follows rather than drifting.
 */
export const HOUSE_PALETTE: HousePalette = {
  gold: '#e8c37a',
  goldBright: '#ffd98a',
  cream: '#f5efe2',
  ink: '#08150f',
  danger: '#e2604a',
  ok: '#6fd08c',
  seatEmpty: 'rgba(245, 239, 226, 0.12)',
};

/** The house corner and type, same rule as the palette. */
export const HOUSE_RADIUS = 16;
export const HOUSE_FONTS: HouseFonts = {
  display: ['Georgia', 'Times New Roman', 'serif'],
  ui: ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif'],
};

/**
 * The theme a screen stands in, leniently: each part missing or unknown
 * reads as what the house ships (`readTheme`), so a junk payload can never
 * fail the render — and `themeTokens(null)` is the defaults themselves.
 */
export function themeTokens(raw: unknown): ThemeTokens {
  const theme: CasinoTheme = readTheme(raw);
  const felt = FELTS[theme.felt];
  const deck = DECKS[theme.deck];
  return {
    felt: {
      id: theme.felt,
      deep: felt.deep,
      deepRgb: felt.deepRgb,
      base: felt.base,
      light: felt.light,
    },
    chips: { id: theme.chips, faces: CHIP_SETS[theme.chips].faces },
    deck: {
      id: theme.deck,
      back: {
        ground: deck.back.ground,
        pattern: deck.back.pattern,
        backPattern: deck.back.backPattern,
        border: deck.back.border,
      },
      face: { paper: deck.face.paper, edge: deck.face.edge, suits: { ...deck.face.suits } },
    },
    palette: HOUSE_PALETTE,
    radius: HOUSE_RADIUS,
    font: HOUSE_FONTS,
  };
}
